import {
  generateKeyPair,
  computeThumbprint,
  signNonce,
  base64UrlDecode,
  base64UrlEncode,
} from './crypto';
import {
  loadDeviceKeyPair,
  saveDeviceKeyPair,
  clearDeviceKeyPair,
} from './storage';

// Fachada do módulo workstationAuth: junta crypto + storage e expõe as ações que
// o fluxo de login consome (carregar keypair, gerar+persistir, assinar nonce,
// pedir geolocalização para o cadastro).
//
// Existe um único par de chaves por navegador, reaproveitado por qualquer
// usuário que fizer login nesta máquina — por isso as funções abaixo não
// recebem `username`. O esquema antigo (indexado por e-mail) foi removido de
// `storage.js` (ver comentário lá).

export const getLocalKeyPair = () => loadDeviceKeyPair();

// Gera uma keypair nova e a persiste. Chamada apenas no momento em que o
// usuário aceita cadastrar esta estação de trabalho — evita criar lixo no
// IndexedDB para visitantes anônimos da tela de login.
export const createAndPersistKeyPair = async () => {
  const { privateKey, publicKey, publicKeyJwk } = await generateKeyPair();
  const thumbprint = await computeThumbprint(publicKeyJwk);
  await saveDeviceKeyPair({
    privateKey,
    publicKey,
    publicKeyJwk,
    thumbprint,
  });
  return { privateKey, publicKey, publicKeyJwk, thumbprint };
};

// Reaproveita a keypair existente quando o usuário precisa pedir aprovação
// de novo (ex.: solicitação anterior foi rejeitada) em vez de gerar outra —
// ou quando outro usuário faz login na mesma máquina.
export const ensureKeyPair = async () => {
  const existing = await loadDeviceKeyPair();
  if (existing) return existing;
  return createAndPersistKeyPair();
};

export const removeKeyPair = () => clearDeviceKeyPair();

// Descarta a keypair local e gera outra. Chamada quando a pessoa pede uma nova
// aprovação depois de o backend recusar a estação: a chave anterior foi
// revogada ou expirou, e insistir nela só reproduz a recusa — o backend ainda
// reconhece o thumbprint revogado num atalho legado do cadastro e responde
// `Approved` sem criar solicitação, prendendo o fluxo em loop.
export const rotateKeyPair = async () => {
  await clearDeviceKeyPair();
  return createAndPersistKeyPair();
};

export const signWithLocalKey = (privateKey, nonceB64u) =>
  signNonce(privateKey, nonceB64u);

// Pedida só no cadastro da estação de trabalho — ajuda o supervisor a decidir
// se aprova a solicitação. Em caso de erro/negação o fluxo deve bloquear o
// envio em vez de mandar coordenadas nulas (backend exige lat/lng).
export const getGeolocation = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('GEOLOCATION_UNSUPPORTED'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      position =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
      error => reject(error),
      { timeout: 10000, enableHighAccuracy: false },
    );
  });

export const getWorkstationInfo = () => ({
  userAgent:
    typeof navigator !== 'undefined' ? navigator.userAgent || null : null,
});

export { base64UrlDecode, base64UrlEncode, computeThumbprint };
export {
  WORKSTATION_AUTH_STATUS,
  normalizeWorkstationAuthStatus,
} from './constants';
